import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { syncService } from '../services/syncService';
import { SyncStatus, MissingProduct } from '../types';

interface SyncState {
  // Статус синхронизации
  syncStatus: SyncStatus | null;
  isLoadingStatus: boolean;
  statusError: string | null;
  statusLastFetch: number | null;

  // Товары, которых нет в БД
  missingProducts: MissingProduct[];
  isLoadingMissing: boolean;
  missingError: string | null;
  missingLastFetch: number | null;

  // Общие флаги
  isAnyLoading: boolean;
}

interface SyncActions {
  // Действия для статуса синхронизации
  fetchSyncStatus: () => Promise<void>;
  clearSyncStatus: () => void;

  // Действия для товаров, которых нет в БД
  fetchMissingProducts: (warehouseId: number) => Promise<void>;
  clearMissingProducts: () => void;

  // Общие действия
  clearAll: () => void;
  isDataStale: (type: 'status' | 'missing', maxAgeMinutes?: number) => boolean;
}

type SyncStore = SyncState & SyncActions;

export const useSyncStore = create<SyncStore>()(
  persist(
    (set, get) => ({
      // Начальное состояние
      syncStatus: null,
      isLoadingStatus: false,
      statusError: null,
      statusLastFetch: null,

      missingProducts: [],
      isLoadingMissing: false,
      missingError: null,
      missingLastFetch: null,

      isAnyLoading: false,

      // Действия для статуса синхронизации
      fetchSyncStatus: async () => {
        const { isAnyLoading } = get();
        if (isAnyLoading) {
          console.warn('Другой запрос уже выполняется');
          return;
        }

        set({ isLoadingStatus: true, statusError: null, isAnyLoading: true });
        try {
          const response = await syncService.getSyncStatus();
          set({ 
            syncStatus: response.data, 
            isLoadingStatus: false,
            statusError: null,
            statusLastFetch: Date.now(),
            isAnyLoading: false
          });
        } catch (error) {
          const errorMessage = error instanceof Error ? error.message : 'Неизвестная ошибка';
          set({ 
            isLoadingStatus: false, 
            statusError: errorMessage,
            isAnyLoading: false
          });
        }
      },

      clearSyncStatus: () => {
        set({ 
          syncStatus: null, 
          statusError: null, 
          statusLastFetch: null 
        });
      },

      // Действия для товаров, которых нет в БД
      fetchMissingProducts: async (warehouseId: number) => {
        const { isAnyLoading } = get();
        if (isAnyLoading) {
          console.warn('Другой запрос уже выполняется');
          return;
        }

        set({ isLoadingMissing: true, missingError: null, isAnyLoading: true });
        try {
          const response = await syncService.getMissingProducts(warehouseId);
          console.log('Store received response:', response);
          set({ 
            missingProducts: response.missing_products, 
            isLoadingMissing: false,
            missingError: null,
            missingLastFetch: Date.now(),
            isAnyLoading: false
          });
        } catch (error) {
          const errorMessage = error instanceof Error ? error.message : 'Неизвестная ошибка';
          set({ 
            isLoadingMissing: false, 
            missingError: errorMessage,
            isAnyLoading: false
          });
        }
      },

      clearMissingProducts: () => {
        set({ 
          missingProducts: [], 
          missingError: null, 
          missingLastFetch: null 
        });
      },

      // Общие действия
      clearAll: () => {
        set({
          syncStatus: null,
          isLoadingStatus: false,
          statusError: null,
          statusLastFetch: null,
          missingProducts: [],
          isLoadingMissing: false,
          missingError: null,
          missingLastFetch: null,
          isAnyLoading: false,
        });
      },

      isDataStale: (type: 'status' | 'missing', maxAgeMinutes = 5) => {
        const state = get();
        const now = Date.now();
        const maxAge = maxAgeMinutes * 60 * 1000;

        if (type === 'status') {
          return !state.statusLastFetch || (now - state.statusLastFetch) > maxAge;
        }
        
        if (type === 'missing') {
          return !state.missingLastFetch || (now - state.missingLastFetch) > maxAge;
        }

        return true;
      },
    }),
    {
      name: 'sync-store',
      partialize: (state) => ({
        syncStatus: state.syncStatus,
        statusLastFetch: state.statusLastFetch,
        missingProducts: state.missingProducts,
        missingLastFetch: state.missingLastFetch,
      }),
    }
  )
);